<template>
  <SoDialog v-model="value" :max-width="800" card-title="Renewal Quote">
    <SoAlert
      v-if="
        renewals.showRateChangeBanner &&
        renewals.quoteModalTariffTableData.containsVariableTariff
      "
      :title="unitRateChangeAlertTitle"
      type="info"
      data-testid="unit-rate-change-alert"
      class="mb-4"
    />
    <div ref="tables">
      <div v-for="(tariffs, title) in tariffTableItems" :key="title">
        <h5 v-if="tariffs.length" class="mb-2">{{ title }}</h5>
        <SoSimpleTable
          v-for="(tariff, index) of tariffs"
          :key="index"
          class="mb-4"
          :headers="tariffHeaders"
          :items="[tariff]"
          is-pivoted
          equal-width-columns
          outlined
        />
      </div>

      <h5 class="mb-2">Renewal Information</h5>
      <SoSimpleTable
        :headers="informationTableHeaders"
        :items="informationTableItems"
        data-testid="renewalInfoTable"
        is-pivoted
        equal-width-columns
      />
      <p class="my-2" data-testid="vat-inclusive-note">
        {{ vatInclusiveNote }}
      </p>
      <p class="my-2">
        Please be aware that the tariff we offer is subject to change, and the
        quote I have provided today may not be available in the future
      </p>
    </div>
    <SoCopyToClipboard class="ml-auto" :element-to-copy="tables" />
  </SoDialog>
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import dayjs from "dayjs"
  import SoAlert from "@/components/SoAlert.vue"
  import SoDialog from "@/components/SoDialog.vue"
  import SoSimpleTable from "@/components/SoSimpleTable.vue"
  import SoCopyToClipboard from "@/components/SoCopyToClipboard.vue"
  import { useRenewalsStore } from "@/stores/renewals"
  import { useCurrentAccountStore } from "@/stores/currentAccount"
  import { formatMoney } from "@/helpers/moneyHelpers"
  import { formatDate } from "@/helpers/dateHelper"
  import {
    FuelVatRate,
    carriedVatRate,
    vatInclusiveNote,
    vatRatePercentLabel,
  } from "@/helpers/vatHelper"
  import { ITariff } from "@/models/tariff/tariff.types"
  import { FeaturesService } from "@/services/admin/FeaturesService"

  export default defineComponent({
    components: {
      SoDialog,
      SoSimpleTable,
      SoCopyToClipboard,
      SoAlert,
    },
    props: {
      modelValue: {
        type: Boolean,
        default: false,
        required: true,
      },
    },
    emits: ["update:modelValue"],
    data() {
      return {
        renewals: useRenewalsStore(),
        current: useCurrentAccountStore(),
        tables: null as HTMLElement | null,
        formatMoney,
        tariffHeaders: [
          "Meterpoint",
          "Estimated Usage",
          "Tariff",
          "Rate Type",
          "Unit Rates",
          "Standing Charge",
          "Duration",
          "Early Termination Charge",
        ],
      }
    },
    computed: {
      value: {
        get() {
          return this.modelValue
        },
        set(value: boolean) {
          this.$emit("update:modelValue", value)
        },
      },
      showElecVatRateChangeInfo(): boolean {
        return (
          FeaturesService.isFeatureActive("SHOW_ELEC_VAT_RATE_CHANGE_INFO") ??
          false
        )
      },
      // Only the tariffs this modal prices: the selected ones it tabulates, plus
      // the current ones behind its cost and exit-fee totals.
      displayedVatRates(): FuelVatRate[] {
        const tariffs = [
          ...Object.values(this.renewals.selectedTariffs),
          ...Object.values(this.renewals.meterpointTariffs).map(
            (mpt) => mpt.currentTariff,
          ),
        ].filter((tariff): tariff is ITariff => !!tariff && !tariff.isExport)

        const byFuel = (fuel: FuelVatRate["fuel"], isGas: boolean) => {
          const fuelTariffs = tariffs.filter((tariff) => tariff.isGas === isGas)
          return fuelTariffs.length
            ? [{ fuel, rate: carriedVatRate(fuelTariffs) }]
            : []
        }
        return [...byFuel("Electricity", false), ...byFuel("Gas", true)]
      },
      vatInclusiveNote(): string {
        const entryOf = (fuel: FuelVatRate["fuel"]) =>
          this.displayedVatRates.find((rates) => rates.fuel === fuel)
        const elecRate = entryOf("Electricity")?.rate
        const gas = entryOf("Gas")

        // The transitional copy names every rate it shows outright, so it can
        // only run on data that gives each displayed fuel a rate — never on a
        // rate assumed on the data's behalf. A gas-less account has no gas rate
        // to give, which is not the same as gas withholding one.
        if (
          this.showElecVatRateChangeInfo &&
          elecRate !== undefined &&
          (!gas || gas.rate !== undefined)
        ) {
          const onGas =
            gas?.rate === undefined
              ? ""
              : ` and ${vatRatePercentLabel(gas.rate)} on gas`
          return `Prices shown include VAT at ${vatRatePercentLabel(elecRate)} on electricity${onGas}. Until 1 October, you'll still pay electricity VAT at 5%.`
        }
        return vatInclusiveNote(this.displayedVatRates)
      },
      formattedAccountBalance() {
        const balance = formatMoney({
          amount: this.current.account.data.balance,
          showNegativeSign: false,
        })

        return String(`${balance} ${this.accountBalanceStatus}`).trim()
      },
      accountBalanceStatus() {
        return this.current.account.data.balanceStatus.text
      },
      informationTableHeaders() {
        const accountBalanceHeader = `Include Account Balance (${this.formattedAccountBalance})`
        const exitFeeHeader = `Early Termination Charges Applicable (${formatMoney(
          { amount: this.renewals.exitFeeTotal },
        )})`

        return [
          "Tariff Renewal Date",
          "Estimated Annual Cost",
          accountBalanceHeader,
          exitFeeHeader,
          "Adjusted Annual Cost",
          "Suggested Monthly Payments",
        ]
      },
      informationTableItems() {
        const tableItems: Record<string, string> = {
          renewalDate: formatDate(dayjs(this.renewals.changeDate)),
          estimatedAnnualCost: formatMoney({
            amount: this.renewals.selectedAgreementsCostTotal,
          }),
          includeAccountBalance: this.renewals.includeAccountBalance
            ? "Yes"
            : "No",
          exitFeeApplicable: this.renewals.includeExitFee ? "Yes" : "No",
          adjustedAnnualCost: formatMoney({
            amount: this.renewals.adjustedAnnualCost,
          }),
          suggestedMonthlyPayments: `Equal: ${formatMoney({
            amount: this.renewals.suggestedPaymentAmount,
          })}\nSeasonal: ${formatMoney({
            amount: Math.round(this.renewals.suggestedPaymentAmount) * 0.75,
          })} Summer, ${formatMoney({
            amount: Math.round(this.renewals.suggestedPaymentAmount) * 1.25,
          })} Winter`,
        }

        return [Object.values(tableItems)]
      },
      tariffTableItems() {
        return {
          "Tariffs being renewed":
            this.renewals.quoteModalTariffTableData.agreementsChanging,
          "Tariffs not being renewed":
            this.renewals.quoteModalTariffTableData.agreementsNotChanging,
        }
      },
      unitRateChangeAlertTitle() {
        const date = formatDate(dayjs().add(1, "month").startOf("month"))
        return `Variable tariff rates are effective from ${date}`
      },
    },
    watch: {
      modelValue(value) {
        if (value) {
          this.$nextTick(() => {
            this.tables = this.$refs["tables"] as HTMLElement
          })
        }
      },
    },
  })
</script>
